<template>
  <!-- SPEC-CHINA-ECOUNT-PRODUCTS-TRANSACTIONS-DASHBOARD-001 — 거래 대시보드 (DealerGsp 패턴 답습) -->
  <div class="transactions-dashboard">
    <!-- 에러 배너 -->
    <a-alert
      v-if="errorMsg && !loading"
      type="warning"
      :message="errorMsg"
      style="margin-bottom: 12px;"
      show-icon
      closable
      @close="$emit('retry')"
    >
      <template #action>
        <a-button size="small" @click="$emit('retry')">{{ $t('ecountEtl_common_retry') }}</a-button>
      </template>
    </a-alert>

    <!-- KPI 카드 3개 -->
    <div class="kpi-row">
      <a-card class="kpi-card">
        <div v-if="!loading">
          <div class="kpi-label">{{ $t('ecountEtl_transactions_kpi_total') }}</div>
          <div class="kpi-value">{{ formatNumber(stats.kpi.totalTransactions) }}</div>
        </div>
        <a-skeleton v-else active :paragraph="{ rows: 1 }" />
      </a-card>
      <a-card class="kpi-card">
        <div v-if="!loading">
          <div class="kpi-label">{{ $t('ecountEtl_transactions_kpi_totalQty') }}</div>
          <div class="kpi-value">{{ formatNumber(stats.kpi.totalQuantity) }}</div>
        </div>
        <a-skeleton v-else active :paragraph="{ rows: 1 }" />
      </a-card>
      <a-card class="kpi-card">
        <div v-if="!loading">
          <div class="kpi-label">{{ $t('ecountEtl_transactions_kpi_activeCustomers') }}</div>
          <div class="kpi-value">{{ formatNumber(stats.kpi.activeCustomers) }}</div>
        </div>
        <a-skeleton v-else active :paragraph="{ rows: 1 }" />
      </a-card>
    </div>

    <!-- 차트 행 1: 월별 + 거래타입 -->
    <div class="chart-row">
      <!-- 월별 거래량 -->
      <a-card class="chart-card" :title="$t('ecountEtl_transactions_chart_monthly')">
        <div v-if="loading" class="chart-skeleton">
          <a-skeleton active :paragraph="{ rows: 4 }" />
        </div>
        <div v-else-if="stats.monthly.length === 0" class="chart-empty">
          <a-empty :description="$t('ecountEtl_common_noData')" />
        </div>
        <div v-else class="chart-container" role="img" :aria-label="$t('ecountEtl_transactions_chart_monthly')">
          <Bar :data="monthlyChartData" :options="monthlyChartOptions" />
        </div>
      </a-card>

      <!-- 거래 타입별 -->
      <a-card class="chart-card" :title="$t('ecountEtl_transactions_chart_txType')">
        <div v-if="loading" class="chart-skeleton">
          <a-skeleton active :paragraph="{ rows: 4 }" />
        </div>
        <div v-else-if="stats.transactionTypeBreakdown.length === 0" class="chart-empty">
          <a-empty :description="$t('ecountEtl_common_noData')" />
        </div>
        <div v-else class="chart-container" role="img" :aria-label="$t('ecountEtl_transactions_chart_txType')">
          <Doughnut :data="txTypeChartData" :options="txTypeChartOptions" />
        </div>
      </a-card>
    </div>

    <!-- 차트 행 2: topCustomers + topProducts -->
    <div class="chart-row" style="margin-top: 12px;">
      <!-- 거래처 top10 — chart 클릭 → multi-select toggle -->
      <a-card class="chart-card" :title="$t('ecountEtl_transactions_chart_topCustomers')">
        <div v-if="loading" class="chart-skeleton">
          <a-skeleton active :paragraph="{ rows: 4 }" />
        </div>
        <div v-else-if="stats.topCustomers.length === 0" class="chart-empty">
          <a-empty :description="$t('ecountEtl_common_noData')" />
        </div>
        <div v-else class="chart-container" role="img" :aria-label="$t('ecountEtl_transactions_chart_topCustomers')">
          <Bar :data="topCustomersChartData" :options="topCustomersChartOptions" />
        </div>
      </a-card>

      <!-- 제품 top10 — chart 클릭 → ecountProductCode 필터 toggle -->
      <a-card class="chart-card" :title="$t('ecountEtl_transactions_chart_topProducts')">
        <div v-if="loading" class="chart-skeleton">
          <a-skeleton active :paragraph="{ rows: 4 }" />
        </div>
        <div v-else-if="stats.topProducts.length === 0" class="chart-empty">
          <a-empty :description="$t('ecountEtl_common_noData')" />
        </div>
        <div v-else class="chart-container" role="img" :aria-label="$t('ecountEtl_transactions_chart_topProducts')">
          <Bar :data="topProductsChartData" :options="topProductsChartOptions" />
        </div>
      </a-card>
    </div>
  </div>
</template>

<script>
// @MX:NOTE: [AUTO] SPEC-CHINA-ECOUNT-PRODUCTS-TRANSACTIONS-DASHBOARD-001 — 거래 대시보드 컴포넌트 (DealerGsp SalesDashboard 패턴 답습)
// @MX:SPEC: SPEC-CHINA-ECOUNT-PRODUCTS-TRANSACTIONS-DASHBOARD-001
import { Bar, Doughnut } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const EMPTY_STATS = {
  kpi: { totalTransactions: 0, totalQuantity: 0, activeCustomers: 0 },
  monthly: [],
  transactionTypeBreakdown: [],
  topCustomers: [],
  topProducts: [],
};

const DONUT_COLORS = [
  'rgba(22, 119, 255, 0.85)',
  'rgba(82, 196, 26, 0.85)',
  'rgba(250, 140, 22, 0.85)',
  'rgba(255, 77, 79, 0.85)',
  'rgba(114, 46, 209, 0.85)',
  'rgba(19, 194, 194, 0.85)',
  'rgba(250, 219, 20, 0.85)',
  'rgba(245, 106, 0, 0.85)',
];

export default {
  name: 'TransactionsDashboard',
  components: { Bar, Doughnut },

  props: {
    statsData: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    errorMsg: { type: String, default: '' },
    // 현재 선택된 customerCodes (강조 표시용)
    selectedCustomerCodes: { type: Array, default: () => [] },
    // 현재 선택된 ecountProductCode
    activeProductCode: { type: String, default: '' },
    // 현재 선택된 transactionType
    activeTransactionType: { type: String, default: '' },
  },

  emits: ['retry', 'chart-filter'],

  computed: {
    stats() {
      return this.statsData || EMPTY_STATS;
    },

    // 월별 bar chart
    monthlyChartData() {
      const monthly = [...(this.stats.monthly || [])].sort((a, b) => a.month.localeCompare(b.month));
      const labels = monthly.map((m) => m.month);
      return {
        labels,
        datasets: [{
          label: this.$t('ecountEtl_transactions_chart_monthly'),
          data: monthly.map((m) => m.quantity),
          backgroundColor: 'rgba(22, 119, 255, 0.85)',
          borderColor: 'rgba(22, 119, 255, 1)',
          borderWidth: 1,
        }],
      };
    },

    monthlyChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { mode: 'index', intersect: false },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { precision: 0 },
            title: { display: true, text: this.$t('ecountEtl_chart_axis_qty') },
          },
          x: {
            title: { display: true, text: this.$t('ecountEtl_chart_axis_month') },
          },
        },
      };
    },

    // 거래 타입 도넛 chart
    txTypeChartData() {
      const items = this.stats.transactionTypeBreakdown || [];
      return {
        labels: items.map((t) => this.txTypeLabel(t.transactionType)),
        datasets: [{
          data: items.map((t) => t.quantity),
          backgroundColor: items.map((_, i) => DONUT_COLORS[i % DONUT_COLORS.length]),
          borderWidth: 1,
        }],
      };
    },

    txTypeChartOptions() {
      const self = this;
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true, position: 'right' },
          tooltip: { mode: 'index', intersect: false },
        },
        onClick: (event, elements) => {
          if (!elements || elements.length === 0) return;
          const idx = elements[0].index;
          const items = self.stats.transactionTypeBreakdown || [];
          const txType = items[idx]?.transactionType;
          if (!txType) return;
          self.$emit('chart-filter', { type: 'transactionType', value: txType });
        },
      };
    },

    // @MX:NOTE: [AUTO] AC-FE-T-7 — chart 라벨 이름 우선 (customerName primary, customerCode secondary)
    // REQ-FE-T-010 강제: code 단독 노출 금지
    topCustomersChartData() {
      const top = (this.stats.topCustomers || []).slice(0, 10);
      const selected = this.selectedCustomerCodes || [];
      // 이름 우선 라벨 — customerName(trim) || customerCode(trim) || '(미분류)' i18n fallback
      // 공백 문자열도 falsy로 취급해 빈 라벨 노출 방지 (사용자 보고 #2 fix)
      const unknownLabel = this.$t('ecountEtl_chart_unknown_label');
      const labels = top.map((c) => this._buildCustomerLabel(c, unknownLabel));
      const bg = top.map((c) =>
        selected.length === 0 || selected.includes(c.customerCode)
          ? 'rgba(250, 140, 22, 0.85)'
          : 'rgba(250, 140, 22, 0.3)',
      );
      return {
        labels,
        datasets: [{
          label: this.$t('ecountEtl_chart_axis_qty'),
          data: top.map((c) => c.quantity),
          backgroundColor: bg,
          borderColor: bg.map((b) => b.replace('0.85', '1').replace('0.3', '0.5')),
          borderWidth: 1,
        }],
      };
    },

    topCustomersChartOptions() {
      const self = this;
      return {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              // tooltip: customerName (customerCode) — 이름+코드 보조
              label: (ctx) => {
                const top = (self.stats.topCustomers || []).slice(0, 10);
                const item = top[ctx.dataIndex];
                if (!item) return `Qty: ${ctx.parsed.x}`;
                const unknownLabel = self.$t('ecountEtl_chart_unknown_label');
                const name = self._buildCustomerLabel(item, unknownLabel);
                const code = (item.customerCode || '').trim();
                return `${name}${code && name !== code ? ` (${code})` : ''}: ${ctx.parsed.x}`;
              },
            },
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            ticks: { precision: 0 },
            title: { display: true, text: this.$t('ecountEtl_chart_axis_qty') },
          },
          y: {
            ticks: {
              callback: (val, idx) => {
                // labels와 동일한 fallback 유지: customerName(trim) || customerCode(trim) || '(미분류)'
                const top = (self.stats.topCustomers || []).slice(0, 10);
                const item = top[idx];
                if (!item) return '';
                const unknownLabel = self.$t('ecountEtl_chart_unknown_label');
                const label = self._buildCustomerLabel(item, unknownLabel);
                return label.length > 14 ? label.slice(0, 14) + '…' : label;
              },
            },
          },
        },
        onClick: (event, elements) => {
          if (!elements || elements.length === 0) return;
          const idx = elements[0].index;
          const top = (self.stats.topCustomers || []).slice(0, 10);
          const customerCode = top[idx]?.customerCode;
          if (!customerCode) return;
          self.$emit('chart-filter', { type: 'customerCode', value: customerCode });
        },
      };
    },

    // @MX:NOTE: [AUTO] AC-FE-T-7 — chart 라벨 이름 우선 (productDescription primary, ecountProductCode secondary)
    topProductsChartData() {
      const top = (this.stats.topProducts || []).slice(0, 10);
      const activeCode = this.activeProductCode;
      // 이름 우선 라벨 — productDescription || ecountProductCode || '-'
      const labels = top.map((p) => p.productDescription || p.ecountProductCode || '-');
      const bg = top.map((p) =>
        !activeCode || p.ecountProductCode === activeCode
          ? 'rgba(82, 196, 26, 0.85)'
          : 'rgba(82, 196, 26, 0.3)',
      );
      return {
        labels,
        datasets: [{
          label: this.$t('ecountEtl_chart_axis_qty'),
          data: top.map((p) => p.quantity),
          backgroundColor: bg,
          borderColor: bg.map((b) => b.replace('0.85', '1').replace('0.3', '0.5')),
          borderWidth: 1,
        }],
      };
    },

    topProductsChartOptions() {
      const self = this;
      return {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const top = (self.stats.topProducts || []).slice(0, 10);
                const item = top[ctx.dataIndex];
                if (!item) return `Qty: ${ctx.parsed.x}`;
                const name = item.productDescription || item.ecountProductCode || '-';
                const code = item.ecountProductCode || '';
                return `${name}${code && name !== code ? ` (${code})` : ''}: ${ctx.parsed.x}`;
              },
            },
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            ticks: { precision: 0 },
            title: { display: true, text: this.$t('ecountEtl_chart_axis_qty') },
          },
          y: {
            ticks: {
              callback: (val, idx) => {
                const top = (self.stats.topProducts || []).slice(0, 10);
                const label = top[idx]?.productDescription || top[idx]?.ecountProductCode || '';
                return label.length > 14 ? label.slice(0, 14) + '…' : label;
              },
            },
          },
        },
        onClick: (event, elements) => {
          if (!elements || elements.length === 0) return;
          const idx = elements[0].index;
          const top = (self.stats.topProducts || []).slice(0, 10);
          const productCode = top[idx]?.ecountProductCode;
          if (!productCode) return;
          self.$emit('chart-filter', { type: 'productCode', value: productCode });
        },
      };
    },
  },

  methods: {
    formatNumber(v) {
      if (v === null || v === undefined || v === '') return '-';
      const n = Number(v);
      if (!Number.isFinite(n)) return String(v);
      return n.toLocaleString(this.$i18n?.locale || 'zh-CN', { maximumFractionDigits: 0 });
    },
    // 거래 타입 enum → 다국어 표시 라벨. 미정의 값은 원문 유지(BE 신규 enum도 안전)
    txTypeLabel(type) {
      if (!type) return '-';
      const key = `ecountTx_txType_${type}`;
      return this.$te(key) ? this.$t(key) : type;
    },
    // 인기 거래처 차트 라벨 통일 fallback — labels/ticks/tooltip 일관성 보장
    // 공백 trim 후 customerName → customerCode → i18n unknown 순서
    _buildCustomerLabel(item, unknownLabel) {
      if (!item) return unknownLabel;
      const name = (item.customerName || '').trim();
      if (name) return name;
      const code = (item.customerCode || '').trim();
      if (code) return code;
      return unknownLabel;
    },
  },
};
</script>

<style scoped>
.transactions-dashboard {
  margin-bottom: 16px;
}

/* KPI 카드 행 */
.kpi-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.kpi-card {
  text-align: center;
}

.kpi-label {
  font-size: 13px;
  color: #8c8c8c;
  margin-bottom: 8px;
}

.kpi-value {
  font-size: 28px;
  font-weight: 700;
  color: #1677ff;
}

/* 차트 행 */
.chart-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.chart-card {
  min-height: 280px;
}

.chart-container {
  height: 220px;
  overflow-x: auto;
}

.chart-skeleton,
.chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
}

/* 모바일 375px */
@media (max-width: 480px) {
  .kpi-row {
    grid-template-columns: 1fr;
  }

  .kpi-value {
    font-size: 22px;
  }

  .chart-row {
    grid-template-columns: 1fr;
  }

  .chart-container {
    height: 200px;
    overflow-x: auto;
  }
}
</style>
